import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { LocateFixed, Plus, Save } from 'lucide-react'
import { useState } from 'react'
import { ChoiceGroup } from '@/components/form/choice'
import { NotesField } from '@/components/form/notes-field'
import { PhotoCapture } from '@/components/media/photo-capture'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/field'
import { PURPOSES, queueVisit, type VisitDraft } from '@/features/visits/visits'
import { db } from '@/offline/db'

export const Route = createFileRoute('/_app/visits/new')({ component: NewVisit })

function NewVisit() {
  const { me } = Route.useRouteContext()
  const navigate = useNavigate()
  const [v, setV] = useState<VisitDraft>(() => ({
    clientUuid: crypto.randomUUID(),
    purpose: '',
    companyName: '',
    contactName: '',
    notes: '',
    photoIds: [],
    visitedAt: new Date().toISOString(),
  }))
  const [saved, setSaved] = useState(false)
  const [locating, setLocating] = useState(false)

  const locate = () => {
    if (!navigator.geolocation) return
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLocating(false)
        setV((x) => ({
          ...x,
          lat: Number(p.coords.latitude.toFixed(6)),
          lng: Number(p.coords.longitude.toFixed(6)),
        }))
      },
      () => setLocating(false),
      { timeout: 15000 },
    )
  }

  const save = async () => {
    await queueVisit(v, me.real_user_id)
    setSaved(true)
  }

  if (saved) {
    return (
      <div className="mx-auto flex max-w-xl flex-col gap-4">
        <h1 className="text-3xl uppercase">Visit saved</h1>
        <p className="text-lg">It sends itself when you have signal.</p>
        <Button asChild size="hero">
          <Link to="/reluxes/new">
            <Plus aria-hidden /> Start a Relux from this visit
          </Link>
        </Button>
        <Button variant="outline" size="lg" onClick={() => void navigate({ to: '/' })}>
          Back to Home
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-8 pb-32">
      <h1 className="text-3xl uppercase">Log Site Visit</h1>
      <ChoiceGroup
        label="Why were you there?"
        options={PURPOSES}
        value={v.purpose}
        onChange={(p) => setV({ ...v, purpose: p })}
      />
      <Field label="Company">
        {(f) => (
          <Input
            {...f}
            value={v.companyName}
            onChange={(e) => setV({ ...v, companyName: e.target.value })}
            placeholder="e.g. Build Co"
          />
        )}
      </Field>
      <Field label="Who did you see? (optional)">
        {(f) => (
          <Input
            {...f}
            value={v.contactName}
            onChange={(e) => setV({ ...v, contactName: e.target.value })}
          />
        )}
      </Field>
      {v.lat != null ? (
        <p className="font-semibold text-success-fg">Location saved</p>
      ) : (
        <Button
          variant="outline"
          size="lg"
          className="self-start"
          onClick={locate}
          disabled={locating}
        >
          <LocateFixed aria-hidden /> {locating ? 'Finding you...' : 'Add my location (optional)'}
        </Button>
      )}
      <NotesField label="Notes" value={v.notes} onChange={(n) => setV({ ...v, notes: n })} />
      <PhotoCapture
        userId={me.real_user_id}
        fileIds={v.photoIds}
        onAdd={(ids) => setV((x) => ({ ...x, photoIds: [...x.photoIds, ...ids] }))}
        onRemove={(id) => {
          void db.files.delete(id)
          setV((x) => ({ ...x, photoIds: x.photoIds.filter((p) => p !== id) }))
        }}
      />
      <Button size="lg" onClick={() => void save()} disabled={!v.purpose || me.impersonating}>
        <Save aria-hidden /> Save visit
      </Button>
    </div>
  )
}
